import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SystemTabs } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import SystemBanner from "../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import type { OnboardingJob } from "../../services/backend.service";
import { isOnboardingJobArtifactExpired } from "./OnboardingJobProgress";
import { OnboardingRunsPanel } from "./OnboardingRunsPanel";
import { type OnboardingToolsTab, onboardingWorkflowKey, resolveSelectedOnboardingRun } from "./onboardingJobState";
import { AutoPromptSection } from "./sections/AutoPromptSection";
import { CalculateRecurringSection } from "./sections/CalculateRecurring/CalculateRecurringSection";
import { DataLoadSection } from "./sections/DataLoad/DataLoadSection";
import { DbActionsSection } from "./sections/DbActionsSection";
import { SftpProvisioningSection } from "./sections/SftpProvisioningSection";
import { isOnboardingJobNotFoundError, useOnboardingJob, useOnboardingJobs } from "./useOnboardingJobs";

const isTabKey = (value: string | null): value is OnboardingToolsTab =>
	value === "auto-prompt" || value === "data-load" || value === "recurring" || value === "db" || value === "sftp";

export default function OnboardingToolsPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment?.department_id;
	const [createdJob, setCreatedJob] = useState<OnboardingJob>();
	const jobsQuery = useOnboardingJobs(departmentId);
	const tabParam = searchParams.get("tab");
	// DEFAULT: Auto Prompt is the page's primary starting workflow when no explicit tab was linked.
	const tab: OnboardingToolsTab = isTabKey(tabParam) ? tabParam : "auto-prompt";
	const selectedJobId = searchParams.get("run");
	const listedJob = selectedJobId ? jobsQuery.data?.find((job) => job.id === selectedJobId) : undefined;
	const createdSelectedJob = createdJob?.id === selectedJobId ? createdJob : undefined;
	const shouldFetchSelectedDetail = Boolean(departmentId && selectedJobId && jobsQuery.isSuccess && !listedJob && !createdSelectedJob);
	const selectedDetailQuery = useOnboardingJob(
		shouldFetchSelectedDetail && selectedJobId !== null ? selectedJobId : undefined,
		shouldFetchSelectedDetail ? departmentId : undefined,
	);
	const selectedRunState = departmentId
		? resolveSelectedOnboardingRun({
				selectedJobId,
				departmentId,
				listLoaded: jobsQuery.isSuccess,
				listError: jobsQuery.isError,
				listedJob,
				createdJob: createdSelectedJob,
				detailLoading: shouldFetchSelectedDetail && selectedDetailQuery.isLoading,
				detailError: shouldFetchSelectedDetail && selectedDetailQuery.isError,
				detailNotFound: shouldFetchSelectedDetail && selectedDetailQuery.isError && isOnboardingJobNotFoundError(selectedDetailQuery.error),
				detailJob: selectedDetailQuery.data,
			})
		: { kind: "none" as const };
	const selectedJob = selectedRunState.kind === "resolved" ? selectedRunState.job : undefined;
	const restoredJobMismatch = selectedRunState.kind === "mismatch";
	const selectedRunNotFound = selectedRunState.kind === "not_found";
	const selectedRunResolved = selectedRunState.kind === "none" || selectedRunState.kind === "resolved";
	const selectedArtifactsExpired = selectedJob ? isOnboardingJobArtifactExpired(selectedJob, DateTime.now()) : false;

	const setTab = (nextTab: OnboardingToolsTab) => {
		setSearchParams((previous) => {
			const next = new URLSearchParams(previous);
			next.set("tab", nextTab);
			next.delete("run");
			return next;
		});
		setCreatedJob(undefined);
	};

	const selectJob = (job: OnboardingJob) => {
		setCreatedJob(undefined);
		setSearchParams((previous) => {
			const next = new URLSearchParams(previous);
			next.set("run", job.id);
			next.set("tab", job.origin === "auto_prompt" ? "auto-prompt" : "data-load");
			return next;
		});
	};

	const handleJobCreated = (job: OnboardingJob) => {
		setCreatedJob(job);
		setSearchParams((previous) => {
			const next = new URLSearchParams(previous);
			next.set("run", job.id);
			next.set("tab", job.origin === "auto_prompt" ? "auto-prompt" : "data-load");
			return next;
		});
		void jobsQuery.refetch();
	};

	const clearSelectedRun = (job?: OnboardingJob) => {
		setCreatedJob(undefined);
		setSearchParams((previous) => {
			const next = new URLSearchParams(previous);
			next.delete("run");
			if (job) next.set("tab", job.origin === "auto_prompt" ? "auto-prompt" : "data-load");
			return next;
		});
	};

	const tabs: SystemTab<OnboardingToolsTab>[] = useMemo(
		() => [
			{ label: t("onboarding_tools.tabs.auto_prompt"), value: "auto-prompt" },
			{ label: t("onboarding_tools.tabs.data_load"), value: "data-load" },
			{ label: t("onboarding_tools.tabs.calculate_recurring"), value: "recurring" },
			{ label: t("onboarding_tools.tabs.db_actions"), value: "db" },
			{ label: t("onboarding_tools.tabs.sftp"), value: "sftp" },
		],
		[t],
	);

	return (
		<Box
			data-testid="onboarding-tools-page"
			sx={{
				maxWidth: 1400,
				mx: "auto",
				p: 3,
				pb: 4,
				width: "100%",
				minHeight: "100vh",
				height: "100vh",
				overflow: "auto",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{departmentId && chosenDepartment?.timezone && (
				<OnboardingRunsPanel
					jobs={jobsQuery.data}
					selectedJob={selectedJob}
					timezone={chosenDepartment.timezone}
					isLoading={jobsQuery.isLoading || selectedRunState.kind === "loading"}
					isError={jobsQuery.isError || selectedRunState.kind === "error"}
					onSelect={selectJob}
					onRerun={clearSelectedRun}
				/>
			)}

			<SystemTabs tabs={tabs} currentTab={tab} onTabChange={setTab} dataTestId="onboarding-tools-tabs" sx={{ my: 2, flexShrink: 0 }} />

			<Box sx={{ flex: 1, overflow: "auto", pb: 6 }}>
				{restoredJobMismatch && (
					<SystemBanner
						tone="warning"
						title={t("onboarding_jobs.mismatch_title")}
						description={t("onboarding_jobs.mismatch_description")}
						action={
							<SystemButton buttonVariant="secondary" onClick={() => clearSelectedRun()} data-testid="onboarding-run-mismatch-clear">
								{t("onboarding_jobs.mismatch_action")}
							</SystemButton>
						}
						dataTestId="onboarding-run-mismatch-banner"
					/>
				)}
				{selectedRunNotFound && (
					<SystemBanner
						tone="warning"
						title={t("onboarding_jobs.not_found_title")}
						description={t("onboarding_jobs.not_found_description")}
						action={
							<SystemButton buttonVariant="secondary" onClick={() => clearSelectedRun()} data-testid="onboarding-run-not-found-clear">
								{t("onboarding_jobs.mismatch_action")}
							</SystemButton>
						}
						dataTestId="onboarding-run-not-found-banner"
					/>
				)}
				{departmentId && selectedRunResolved && !restoredJobMismatch && !selectedArtifactsExpired && tab === "auto-prompt" && (
					<AutoPromptSection
						key={onboardingWorkflowKey(departmentId, tab, selectedJobId)}
						restoredJob={selectedJob?.origin === "auto_prompt" ? selectedJob : undefined}
						onJobCreated={handleJobCreated}
					/>
				)}
				{departmentId && selectedRunResolved && !restoredJobMismatch && !selectedArtifactsExpired && tab === "data-load" && (
					<DataLoadSection
						key={onboardingWorkflowKey(departmentId, tab, selectedJobId)}
						restoredJob={selectedJob?.origin === "data_load" ? selectedJob : undefined}
						onJobCreated={handleJobCreated}
					/>
				)}
				{/* Keyed by department: a proposal, its edits and its save selection all belong to the
				    department they were calculated for, and must not survive a switch to another one. */}
				{tab === "recurring" && <CalculateRecurringSection key={chosenDepartment?.department_id} />}
				{tab === "db" && <DbActionsSection />}
				{tab === "sftp" && <SftpProvisioningSection />}
			</Box>
		</Box>
	);
}
